import { CommonModule } from '@angular/common';
import { Component, HostBinding, Input } from '@angular/core';

@Component({
    selector: 'ws-ace-loader',
    templateUrl: './loader.component.html',
    styleUrls: ['./loader.component.scss'],
    imports: [CommonModule],
})
export class AceLoaderComponent {
    @Input() label: string = 'Loading...';

    @HostBinding('attr.opaque')
    @Input()
    opaque: boolean;

    @HostBinding('attr.semiOpaque')
    @Input()
    semiOpaque: boolean;

    @HostBinding('attr.fade')
    @Input()
    fade: boolean;

    /**
     * Controls whether the loader renders at all. Defaults to true so callers that
     * gate visibility with structural directives (*ngIf) keep their existing behavior.
     * Provided for compatibility with the legacy `loading-spinner` component.
     */
    @Input() show: boolean = true;

    @HostBinding('class.ws-ace-loader-hidden')
    get hidden(): boolean {
        return !this.show;
    }

    /**
     * Legacy `loading-spinner` compatibility input. When `true`, the loader fills its
     * (position: relative) parent as an absolute overlay. When `false`, the loader is
     * rendered inline within the document flow using `marginTop`/`marginBottom`.
     */
    @HostBinding('attr.has-overlay')
    @Input()
    hasOverlay: boolean;

    /**
     * Renders the loader as a bare, inline spinner icon that flows within the document like the
     * raw `<i class="fa fa-spinner fa-pulse">` markup it replaces. Strips the absolute overlay,
     * flex centering, padding, backdrop, `z-index`, and (unless `size` is supplied) the `fa-Nx`
     * sizing, and lets the icon inherit its color from the surrounding context.
     *
     * Use this for small spinners inside buttons, typeaheads, form-control feedback slots, and
     * inline status text. Block/overlay callers are unaffected.
     */
    @HostBinding('attr.inline')
    @Input()
    inline: boolean;

    /**
     * Legacy `loading-spinner` compatibility input. When used with an overlay, renders a
     * dark, opaque backdrop with a light spinner.
     */
    @HostBinding('attr.dark-overlay')
    @Input()
    darkOverlay: boolean = true;

    @HostBinding('style.z-index')
    @Input()
    zIndex: number;

    // Margins only apply to the legacy inline (hasOverlay === false) layout, matching the
    // old `loading-spinner` behavior. Absolute-overlay callers are unaffected.
    @HostBinding('style.margin-top')
    get marginTopStyle(): string {
        return this.show && this.hasOverlay === false ? this.top : null;
    }

    @HostBinding('style.margin-bottom')
    get marginBottomStyle(): string {
        return this.show && this.hasOverlay === false ? this.bottom : null;
    }

    @Input()
    set marginTop(marginTop: number) {
        this.top = `${marginTop}px`;
    }

    @Input()
    set marginBottom(marginBottom: number) {
        this.bottom = `${marginBottom}px`;
    }

    @HostBinding('attr.size')
    @Input()
    set size(size: 'small' | number | string) {
        if (size === 'small') {
            this.faSize = 'fa-2x';
            this.sizeSet = true;
        } else if (size !== undefined && size !== null && `${size}`.length) {
            this.faSize = `fa-${size}x`;
            this.sizeSet = true;
        }
    }

    /**
     * Hides the loader's text label. Defaults to `true` so loaders are icon-only by default,
     * matching the overwhelmingly common usage. Set `[hideLabel]="false"` to show the label.
     */
    @HostBinding('attr.hideLabel')
    @Input()
    hideLabel: boolean = true;

    @HostBinding('attr.hideSpinner')
    @Input()
    hideSpinner: boolean;

    faSize: string = 'fa-5x';

    /**
     * The size class applied to the spinner icon. In `inline` mode the icon inherits its natural
     * size (no `fa-Nx`) unless a `size` was explicitly supplied, matching the raw markup it replaces.
     */
    get iconSizeClass(): string {
        return this.inline && !this.sizeSet ? '' : this.faSize;
    }

    private sizeSet: boolean = false;

    // Legacy `loading-spinner` inline-layout default margins.
    top: string = '50px';
    bottom: string = '1px';
}
